import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { UserService } from '@api/custom-services/user.service';
import { EventsControllerList$Params } from '@api/fn/events/events-controller-list';
import { EventSlimDto } from '@api/models';
import { EventsService } from '@api/services';
import { ControlValueAccessorWrapperComponent } from '@shared/components/control-value-accessor-wrapper/control-value-accessor-wrapper.component';
import {
   SelectDataSource,
   SelectField,
   SelectFieldComponent,
} from '@shared/components/select-field/select-field.component';
import { EventStatus } from '@shared/constants/enum';
import { Role } from '@shared/constants/role';
import { EventNameDatePipe } from '@shared/pipes/event-name-date.pipe';
import { map, Observable, of, startWith, switchMap } from 'rxjs';

@Component({
   selector: 'app-select-event-field',
   imports: [CommonModule, ReactiveFormsModule, SelectFieldComponent],
   providers: [EventNameDatePipe],
   template: ` <app-select-field
      [label]="label"
      [placeholder]="placeholder"
      [multiple]="multiple"
      [withSearch]="true"
      [formControl]="formControl"
      [options]="dataSource"
      [selected]="selectedOption$ | async"
   />`,
})
export class SelectEventFieldComponent
   extends ControlValueAccessorWrapperComponent<number | number[]>
   implements ControlValueAccessor, OnInit
{
   @Input() multiple: boolean = false;
   @Input() label?: string;
   @Input() placeholder?: string;
   @Input() onlyUpcoming: boolean = true;
   @Input() packageId?: number;
   @Input() excludeByStatus?: EventStatus[];
   @Input() disableSoldOut: boolean = false;

   dataSource!: SelectDataSource<EventsControllerList$Params>;
   selectedOption$!: Observable<string | null>;
   isSellerUser = false;

   constructor(
      private eventsService: EventsService,
      private eventNameDatePipe: EventNameDatePipe,
      private userService: UserService
   ) {
      super();
   }

   override ngOnInit(): void {
      super.ngOnInit();

      if (!this.label && !this.placeholder) {
         this.label = 'Event';
      }

      this.subs = this.userService.user$.subscribe((user) => {
         this.isSellerUser = (user.roleName as Role) === Role.PROMOTER;
      });

      this.initOptions();
   }

   listToSelectField(list: EventSlimDto[]): SelectField[] {
      return list.map(
         (item) =>
            ({
               id: item.id,
               name: this.eventNameDatePipe.transform(item),
               disabled:
                  this.disableSoldOut &&
                  [EventStatus.SOLD_OUT_SELLERS, EventStatus.SOLD_OUT_ADMINS_ONLY, EventStatus.PAUSE].includes(
                     item.status!
                  ),
               class:
                  this.disableSoldOut &&
                  [EventStatus.SOLD_OUT_SELLERS, EventStatus.SOLD_OUT_ADMINS_ONLY].includes(item.status!)
                     ? 'event-sold-out'
                     : undefined,
            }) as SelectField
      );
   }

   initOptions() {
      this.selectedOption$ = this.formControl.valueChanges.pipe(
         startWith(this.formControl.value),
         map((ids) => (Array.isArray(ids) ? ids : [ids])),
         switchMap((ids) =>
            ids[0]
               ? this.eventsService
                    .eventsControllerGetEventsDetails({ ids })
                    .pipe(map((events) => events.map((event) => event.name).join(', ')))
               : of(null)
         )
      );

      const filters: EventsControllerList$Params = {};

      if (this.onlyUpcoming) {
         filters.upcoming = true;
      }

      if (this.excludeByStatus && this.excludeByStatus.length > 0) {
         filters.excludeByStatus = this.excludeByStatus;
      }

      if (this.packageId) {
         filters.packageId = this.packageId;
      }

      this.dataSource = new SelectDataSource<EventsControllerList$Params>()
         .setInitialFilters(filters)
         .onSearchPageable((params) =>
            this.eventsService.eventsControllerListSlim({ ...filters, ...params }).pipe(
               map((result) => {
                  const currentValue = this.formControl.value;
                  const selectedIds = Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : [];

                  if (selectedIds.length > 0) {
                     return this.eventsService.eventsControllerGetEventsDetails({ ids: selectedIds }).pipe(
                        map((selectedEvents) => ({
                           data: [
                              ...this.listToSelectField(selectedEvents),
                              ...this.listToSelectField(
                                 result.data!.filter((event) => !selectedIds.includes(event.id))
                              ),
                           ],
                           meta: result.meta,
                        }))
                     );
                  }

                  return of({
                     data: this.listToSelectField(result.data!),
                     meta: result.meta,
                  });
               }),
               switchMap((result) => result)
            )
         );
   }
}
